import {
  Box,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useContext, useEffect, useState } from 'react';
import AlertNotationForm from '../../../../shared-components/Widget/Notation/AlertNotationForm';
import EncouragementNotationForm from '../../../../shared-components/Widget/Notation/EncouragementNotationForm';
import NotationWidgetSelection from '../../../../shared-components/Widget/Notation/NotationWidgetSelection';
import NotationWidgetWelcome from '../../../../shared-components/Widget/Notation/NotationWidgetWelcome';
import UpdateNotationForm from '../../../../shared-components/Widget/Notation/UpdateNotationForm';
import { GetRoles } from '../Functions/GetRoles';
import { GetSteps } from '../Functions/GetSteps';
import { NotationWidgetDispatch, NotationWidgetState } from '../NotationWidget';
import { NotationWidgetActionType } from '../Types/NotationWidgetActionType';
import TabPanel from '../../../../shared-components/TabPanel';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.primary}`,
    minWidth: '182px',
  },
  select: {
    minWidth: '150px',
  },
  helperContainer: {
    minHeight: '1.5em',
  },
  grid: {
    backgroundImage:
      'linear-gradient(rgba(150, 150, 150, .3) 1px, transparent 1px), linear-gradient(90deg, rgba(150, 150, 150, .3) 1px, transparent 1px)',
    backgroundSize: '8px 8px, 8px 8px',
    backgroundPosition: '-2px -2px, -2px -2px, -1px -1px, -1px -1px',
  },
}));

function a11yTabProps(index: any) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

const PreviewPanel = () => {
  const classes = useStyles();
  const state = useContext(NotationWidgetState);
  const dispatch = useContext(NotationWidgetDispatch);

  const roles = GetRoles();
  const steps = GetSteps();

  const [activeRole, setActiveRole] = useState(roles[0]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!state.isLoading && state.currentStep.name !== 'Save')
      setActiveTab(state.currentStep.stepNumber);
  }, [state.currentStep]);

  return (
    <Paper elevation={2}>
      <Box style={{ padding: 30, minHeight: '711.5px' }}>
        <Typography variant="h4">Preview</Typography>
        <Grid>
          <Grid item>
            <Grid>
              <Grid item>
                <InputLabel id="role-select">View As</InputLabel>
                <Select
                  labelId="role-select"
                  value={roles.findIndex(x => x.name === activeRole.name)}
                  onChange={async e => {
                    setActiveRole(roles[e.target.value as number]);
                  }}
                  className={classes.select}
                  disabled={state.isLoading}
                >
                  {roles.map((role, index) => (
                    <MenuItem key={index} value={index}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item className={classes.helperContainer}>
                <FormHelperText>{activeRole.helper}</FormHelperText>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Box className={classes.root}>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={activeTab}
                onChange={async (e, i) => {
                  setActiveTab(
                    steps.findIndex(
                      //@ts-ignore
                      x => x.name === (e.target.textContent as string),
                    ),
                  );
                  dispatch({
                    type: NotationWidgetActionType.SetCurrentStep,
                    value:
                      steps[
                        steps.findIndex(
                          //@ts-ignore
                          x => x.name === (e.target.textContent as string),
                        )
                      ],
                  });
                }}
                className={classes.tabs}
              >
                {steps.map(
                  step =>
                    step.name !== 'Save' && (
                      <Tab
                        key={step.key}
                        disabled={state.isLoading}
                        label={step.name}
                      />
                    ),
                )}
              </Tabs>
              <Divider orientation="vertical" variant="fullWidth" flexItem />
              <Box
                display="flex"
                width="100%"
                height="100%"
                minHeight="668px"
                justifyContent="center"
                alignItems="center"
                overflow="scroll"
                className={classes.grid}
              >
                <TabPanel
                  value={activeTab}
                  index={steps.findIndex(x => x.name === 'Welcome Screen')}
                  {...a11yTabProps(steps.findIndex(x => x.key === 0))}
                >
                  <Paper
                    elevation={2}
                    style={{
                      height: !state.isLoading ? state.height : 450,
                      width: !state.isLoading ? state.width : 350,
                      overflow: 'scroll',
                    }}
                  >
                    {state.isLoading ? (
                      <Box
                        height={200}
                        alignContent="center"
                        alignItems="center"
                        justifyContent="center"
                        display="flex"
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <NotationWidgetWelcome
                        isPreview
                        title={state.titleText}
                        description={state.descriptionText}
                        width={state.width}
                      />
                    )}
                  </Paper>
                </TabPanel>
                <TabPanel
                  value={activeTab}
                  index={steps.findIndex(
                    x => x.name === 'Notation Type Select',
                  )}
                  {...a11yTabProps(steps.findIndex(x => x.key === 1))}
                >
                  {(activeRole.value & state.alertPermissions.showAlerts) !==
                    0 ||
                  (activeRole.value & state.updatePermissions.showUpdates) !==
                    0 ||
                  (activeRole.value &
                    state.encouragementPermissions.showEncouragements) !==
                    0 ? (
                    <NotationWidgetSelection
                      isPreview
                      showAlert={
                        (activeRole.value &
                          state.alertPermissions.showAlerts) !==
                        0
                      }
                      showUpdate={
                        (activeRole.value &
                          state.updatePermissions.showUpdates) !==
                        0
                      }
                      showEncouragement={
                        (activeRole.value &
                          state.encouragementPermissions.showEncouragements) !==
                        0
                      }
                    />
                  ) : (
                    <Box
                      height="500px"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography variant="h2">
                        This role cannot create notations via the widget.
                      </Typography>
                    </Box>
                  )}
                </TabPanel>
                <TabPanel
                  value={activeTab}
                  index={steps.findIndex(x => x.name === 'Alerts')}
                  {...a11yTabProps(steps.findIndex(x => x.key === 2))}
                >
                  {(activeRole.value & state.alertPermissions.showAlerts) !==
                  0 ? (
                    <Paper elevation={2}>
                      <Box style={{ padding: 30 }}>
                        <AlertNotationForm
                          reset={() => {}}
                          isPreview
                          alertAddAudiencePermission={
                            (activeRole.value &
                              state.alertPermissions.additionalAudience) !==
                            0
                          }
                          alertAssignToPermission={
                            (activeRole.value &
                              state.alertPermissions.assignTo) !==
                            0
                          }
                          alertAudiencePermission={
                            (activeRole.value &
                              state.alertPermissions.audience) !==
                            0
                          }
                          alertCategoryPermission={
                            (activeRole.value &
                              state.alertPermissions.categories) !==
                            0
                          }
                          alertOccuranceDatePermission={
                            (activeRole.value &
                              state.alertPermissions.occurenceDate) !==
                            0
                          }
                          alertReasonPermission={
                            (activeRole.value &
                              state.alertPermissions.reasons) !==
                            0
                          }
                          alertShowPermission={
                            (activeRole.value &
                              state.alertPermissions.showAlerts) !==
                            0
                          }
                        />
                      </Box>
                    </Paper>
                  ) : (
                    <Box
                      height="500px"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography variant="h2">
                        This role cannot create alerts via the widget.
                      </Typography>
                    </Box>
                  )}
                </TabPanel>
                <TabPanel
                  value={activeTab}
                  index={steps.findIndex(x => x.name === 'Updates')}
                  {...a11yTabProps(steps.findIndex(x => x.key === 3))}
                >
                  {(activeRole.value & state.updatePermissions.showUpdates) !==
                  0 ? (
                    <Paper elevation={2}>
                      <Box style={{ padding: 30 }}>
                        <UpdateNotationForm
                          reset={() => {}}
                          isPreview
                          updateAddAudiencePermission={
                            (activeRole.value &
                              state.updatePermissions.additionalAudience) !==
                            0
                          }
                          updateAudiencePermission={
                            (activeRole.value &
                              state.updatePermissions.audience) !==
                            0
                          }
                          updateCategoryPermission={
                            (activeRole.value &
                              state.updatePermissions.categories) !==
                            0
                          }
                          updateOccurancePermission={
                            (activeRole.value &
                              state.updatePermissions.occurenceDate) !==
                            0
                          }
                          updateReasonPermission={
                            (activeRole.value &
                              state.updatePermissions.reasons) !==
                            0
                          }
                          updateShowPermission={
                            (activeRole.value &
                              state.updatePermissions.showUpdates) !==
                            0
                          }
                        />
                      </Box>
                    </Paper>
                  ) : (
                    <Box
                      height="500px"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography variant="h2">
                        This role cannot create updates via the widget.
                      </Typography>
                    </Box>
                  )}
                </TabPanel>
                <TabPanel
                  value={activeTab}
                  index={steps.findIndex(x => x.name === 'Encouragements')}
                  {...a11yTabProps(steps.findIndex(x => x.key === 4))}
                >
                  {(activeRole.value &
                    state.encouragementPermissions.showEncouragements) !==
                  0 ? (
                    <Paper elevation={2}>
                      <Box style={{ padding: 30 }}>
                        <EncouragementNotationForm
                          reset={() => {}}
                          isPreview
                          encouragementAudiencePermission={
                            (activeRole.value &
                              state.encouragementPermissions.audience) !==
                            0
                          }
                          encouragementShowPermission={
                            (activeRole.value &
                              state.encouragementPermissions
                                .showEncouragements) !==
                            0
                          }
                        />
                      </Box>
                    </Paper>
                  ) : (
                    <Box
                      height="500px"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography variant="h2">
                        This role cannot create encouragements via the widget.
                      </Typography>
                    </Box>
                  )}
                </TabPanel>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};
export default PreviewPanel;
